import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { kitchenService } from "../../services";
import { uploadImage } from "../../utils/imageUploader";
import upload from "../../assets/images/registration/upload.png";
import arrowLeft from "../../assets/images/navbar/arrow-left.png";
import { toast } from "react-toastify";

interface Form1Props {
  onNext: (data: Form1Data) => void;
  initialData?: Form1Data;
}

export interface Form1Data {
  businessName: string;
  ownerName: string;
  businessType: "CLOUD_KITCHEN" | "RESTAURANT";
  primaryContactNumber?: string;
  email: string;
  brandLogo?: string;
  alternateContact?: string;
  otp?: string;
  token?: string;
}

const Form1: React.FC<Form1Props> = ({ onNext, initialData }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<Form1Data>({
    businessName: initialData?.businessName || "",
    ownerName: initialData?.ownerName || "",
    businessType: initialData?.businessType || "CLOUD_KITCHEN",
    email: initialData?.email || "",
    brandLogo: initialData?.brandLogo || "",
    alternateContact: initialData?.alternateContact || "",
    primaryContactNumber: initialData?.primaryContactNumber || localStorage.getItem('verifiedPhoneNumber') || "",
    otp: initialData?.otp || "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [kitchenAuthToken, setKitchenkitchenAuthToken] = useState<string>("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if token already exists (user is editing form after completing step 1)
  useEffect(() => {
    const existingToken = localStorage.getItem("kitchenAuthToken");
    const verifiedPhone = localStorage.getItem("verifiedPhoneNumber");
    
    if (existingToken) {
      setKitchenkitchenAuthToken(existingToken);
      setOtpVerified(true);
      
      // Auto-populate phone number from localStorage if not already set
      if (verifiedPhone && !formData.primaryContactNumber) {
        setFormData((prev) => ({
          ...prev,
          primaryContactNumber: verifiedPhone,
        }));
      }
      
      // If phone number exists, mark OTP as sent and verified
      if (verifiedPhone || initialData?.primaryContactNumber) {
        setOtpSent(true);
      }
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingLogo(true);
    try {
      const imageUrl = await uploadImage(file, 'restaurant', {
        maxSize: 5,
        allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
      });
      
      setFormData((prev) => ({
        ...prev,
        brandLogo: imageUrl,
      }));
      toast.success('Logo uploaded successfully!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = () => {
    setFormData((prev) => ({
      ...prev,
      brandLogo: '',
    }));
    toast.info('Logo removed');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Check if token already exists (editing form after OTP verification)
    const existingToken = localStorage.getItem("kitchenAuthToken");

    // Validation
    if (!formData.businessName.trim()) {
      setError("Business Name is required");
      return;
    }
    if (!formData.ownerName.trim()) {
      setError("Owner Name is required");
      return;
    }
    if (!formData.email.trim()) {
      setError("Email is required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }
    if (!formData.primaryContactNumber?.trim()) {
      setError("Primary Contact Number is required");
      return;
    }

    setLoading(true);
    try {
      // Submit to API with token
      const response: any = await kitchenService.submitStep1(formData);
      toast.success(response.message || "Basic details saved successfully!");
      // Move to next step with token included
      onNext({ ...formData, token: existingToken || kitchenAuthToken });
    } catch (err: any) {
      setError(err.message || "Failed to save data. Please try again.");
      toast.error(err.message || "Failed to save data");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToHome = () => {
    // Clear authentication token and restart the registration flow
    localStorage.removeItem("kitchenAuthToken");
    localStorage.removeItem("kitchenRegistered");
    // Navigate to registration page which will show signin modal
    navigate("/kitchen-registration", { replace: true });
  };

  return (
    <div className="">
      <div className="w-full">
        {/* Back Arrow */}
        <button
          type="button"
          onClick={handleBackToHome}
          className="w-full mb-4 flex items-center justify-between gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <img src={arrowLeft} alt="Back" className="w-10 h-10" />
          <span className="text-sm font-medium">Back to Home</span>
        </button>

        <h2 className="text-[35px] font-Urbanist font-semibold text-gray-900 mb-2 text-left leading-[125%]">
          Setup Your Kitchen
        </h2>
        <p className="text-[#6A6A6A]  text-[15px] font-medium leading-[140%]">
          Enter these details to get started
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5 mt-[20px]">
          {/* Business Name */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="businessName"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Business Name
            </label>
            <input
              type="text"
              id="businessName"
              name="businessName"
              value={formData.businessName}
              onChange={handleChange}
              placeholder="Kitchen’s Name"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
              required
            />
          </div>

          {/* Brand Logo */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="brandLogo"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Brand Logo
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <div 
              onClick={handleLogoClick}
              className="relative flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            >
              <input
                type="text"
                id="brandLogo"
                name="brandLogo"
                value={formData.brandLogo}
                readOnly
                placeholder={uploadingLogo ? "Uploading..." : "Click to upload your brand logo"}
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 transition-all cursor-pointer caret-transparent"
                onFocus={(e) => e.target.blur()}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-gray-50 transition-all"
                disabled={uploadingLogo}
              >
                {uploadingLogo ? (
                  <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <img src={upload} alt="Upload" className="w-[30px] h-[30px]" />
                )}
              </button>
            </div>
            {/* Preview uploaded image */}
            {formData.brandLogo && (
              <div className="relative w-32 h-32 mt-2 border border-gray-200 rounded-lg overflow-hidden group">
                <img
                  src={formData.brandLogo}
                  alt="Brand Logo Preview"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveLogo();
                  }}
                  className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
                  title="Remove logo"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          {/* Business Type */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="businessType"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Business Type
            </label>
            <select
              id="businessType"
              name="businessType"
              value={formData.businessType}
              onChange={handleChange}
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all cursor-pointer"
              required
            >
              <option selected value="" disabled>
                Select business type
              </option>
              <option value="CLOUD_KITCHEN">Cloud Kitchen</option>
              <option value="RESTAURANT">Restaurant</option>
            </select>
          </div>

          {/* Owner Name */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="ownerName"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Name of Kitchen Owner
            </label>
            <input
              type="text"
              id="ownerName"
              name="ownerName"
              value={formData.ownerName}
              onChange={handleChange}
              placeholder="Enter your name"
              className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
              required
            />
          </div>

          {/* Primary Contact Number */}
          <div className="relative">
            <div className="flex flex-col gap-2">
              <label
                htmlFor="primaryContactNumber"
                className="text-sm font-Urbanist font-medium text-gray-700"
              >
                Primary Contact Number {otpVerified && <span className="text-green-600">✓ Verified</span>}
              </label>
              <input
                type="text"
                id="primaryContactNumber"
                name="primaryContactNumber"
                value={formData.primaryContactNumber}
                onChange={handleChange}
                placeholder="Add your mobile number"
                className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
                required
                disabled={otpSent}
              />
            </div>
            {/* <div className="absolute top-0 right-0 cursor-pointer">
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={sendingOtp || otpSent}
                className={`text-[#FF8A00] font-Urbanist text-[20px] font-medium leading-[140%] ${
                  (sendingOtp || otpSent) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {sendingOtp ? "Sending..." : otpSent ? (otpVerified ? "Verified" : "OTP Sent") : "Send OTP"}
              </button>
            </div> */}
          </div>

          {/* OTP */}
          {/* {otpSent && (
            <div className="flex flex-col gap-2">
              <label
                htmlFor="otp"
                className="text-sm font-Urbanist font-medium text-gray-700"
              >
                OTP {otpVerified && <span className="text-green-600">✓ Verified</span>}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  id="otp"
                  name="otp"
                  value={formData.otp}
                  onChange={handleChange}
                  placeholder={`Enter OTP sent to ${formData.primaryContactNumber}`}
                  className="flex-1 px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
                  required
                  disabled={otpVerified}
                />
                {!otpVerified && (
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={loading || !formData.otp}
                    className="px-6 py-3 text-sm font-Urbanist font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-400 rounded-lg hover:from-orange-600 hover:to-orange-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                  >
                    {loading ? "Verifying..." : "Verify"}
                  </button>
                )}
              </div>
            </div>
          )} */}

          {/* Email */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="email"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Email ID
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Add your working email"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
              required
            />
          </div>

          {/* Alternate Contact */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="alternateContact"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Alternate Contact
            </label>
            <input
              type="tel"
              id="alternateContact"
              name="alternateContact"
              value={formData.alternateContact}
              onChange={handleChange}
              placeholder="Secondary contact number"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm mt-2">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-3 mt-2">
            <button
              type="submit"
              className="flex-1 px-6 py-3.5 text-base font-Urbanist font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-400 rounded-lg hover:from-orange-600 hover:to-orange-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98]"
              disabled={loading}
            >
              {loading ? "Saving..." : "Next"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Form1;
